// Blog → Categories list. M5, revised 2026-08-15. Migrated to the console
// component system in M7c / P6.
//
// LIST ONLY — add is `/blog-categories/new`, edit is `/blog-categories/$id`.
//
// A category carries ONE shared name used by both locales (owner ruling
// 2026-08-15), so prefer a name that reads acceptably on the Arabic and the
// English blog alike.

import { Button } from '@fajrarabi/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';

import {
  ConfirmDialog,
  DataTable,
  FilterBar,
  Icon,
  PageHeader,
  StatusBadge,
  actionsColumn,
  sortRows,
  useTableState,
  useToast,
  type Column,
} from '~/components';
import { deleteCategory, listCategories, updateCategory } from '~/lib/blog';

export const Route = createFileRoute('/_authed/blog-categories/')({
  component: BlogCategoriesListPage,
});

type CategoryRow = Awaited<ReturnType<typeof listCategories>>[number];

function BlogCategoriesListPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { toast } = useToast();
  const [pendingDelete, setPendingDelete] = useState<CategoryRow | null>(null);
  const table = useTableState({
    prefix: 'cat',
    defaultSort: { key: 'name', direction: 'asc' },
  });

  const categoriesQuery = useQuery({
    queryKey: ['blog', 'categories'],
    queryFn: ({ signal }) => listCategories(signal),
    staleTime: 30_000,
  });

  const setActive = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      updateCategory(id, { isActive }),
    onSuccess: (_r, vars) => {
      void qc.invalidateQueries({ queryKey: ['blog', 'categories'] });
      toast(vars.isActive ? 'Category visible' : 'Category hidden', { tone: 'success' });
    },
  });

  const removeCategory = useMutation({
    mutationFn: (id: string) => deleteCategory(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['blog', 'categories'] });
      toast('Category deleted', { tone: 'success' });
    },
  });

  const rows = useMemo(() => {
    const all = categoriesQuery.data ?? [];
    const needle = table.q.trim().toLowerCase();
    const filtered = needle
      ? all.filter(
          (c) => c.name.toLowerCase().includes(needle) || c.slug.toLowerCase().includes(needle),
        )
      : all;
    return sortRows(filtered, table.sort, {
      name: (c) => c.name,
      slug: (c) => c.slug,
      isActive: (c) => (c.isActive ? 1 : 0),
    });
  }, [categoriesQuery.data, table.q, table.sort]);

  const columns: Column<CategoryRow>[] = [
    {
      key: 'name',
      header: 'Category',
      sortable: true,
      alwaysVisible: true,
      render: (c) => (
        <div className="min-w-0">
          <p className="truncate font-medium text-admin-900">{c.name}</p>
          {/* Arabic slugs stay Arabic — they are URL-encoded at link time, not
              transliterated. Rendering them raw here is correct. */}
          <p className="mt-0.5 truncate font-mono text-xs text-admin-500">{c.slug}</p>
        </div>
      ),
    },
    {
      key: 'isActive',
      header: 'Visibility',
      sortable: true,
      render: (c) => (
        <StatusBadge
          status={c.isActive ? 'published' : 'hidden'}
          label={c.isActive ? 'Visible' : 'Hidden'}
          dot
        />
      ),
    },
    actionsColumn<CategoryRow>({
      label: (c) => c.name,
      primary: (c) => ({
        id: 'edit',
        label: 'Edit',
        icon: 'edit',
        onSelect: () => void navigate({ to: '/blog-categories/$id', params: { id: c.id } }),
      }),
      actions: (c) => [
        {
          id: 'visibility',
          label: c.isActive ? 'Hide from the blog' : 'Make visible',
          icon: c.isActive ? 'eye' : 'checkCircle',
          onSelect: () => setActive.mutate({ id: c.id, isActive: !c.isActive }),
        },
        {
          id: 'delete',
          label: 'Delete category',
          icon: 'trash',
          destructive: true,
          onSelect: () => setPendingDelete(c),
        },
      ],
    }),
  ];

  return (
    <div className="mx-auto max-w-4xl">
      <PageHeader
        crumbs={[{ label: 'Blog posts', to: '/blog' }, { label: 'Categories' }]}
        title="Blog categories"
        description="One name per category, shown on both the Arabic and English blog."
        actions={
          <Button onClick={() => void navigate({ to: '/blog-categories/new' })}>
            <Icon name="plus" className="h-4 w-4" />
            New category
          </Button>
        }
      />

      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(c) => c.id}
        loading={categoriesQuery.isLoading}
        error={categoriesQuery.error}
        onRetry={() => void categoriesQuery.refetch()}
        sort={table.sort}
        onSortChange={table.setSort}
        onRowClick={(c) => void navigate({ to: '/blog-categories/$id', params: { id: c.id } })}
        caption="Blog categories"
        skeletonRows={4}
        empty={{
          icon: 'tag',
          title: table.isFiltered ? 'No categories match' : 'No categories yet',
          description: table.isFiltered
            ? 'Try clearing the search.'
            : 'Categories group posts on the public blog.',
          action: !table.isFiltered ? (
            <Button onClick={() => void navigate({ to: '/blog-categories/new' })}>
              Add the first category
            </Button>
          ) : undefined,
        }}
        toolbar={
          <FilterBar
            query={table.q}
            onQueryChange={table.setQuery}
            searchPlaceholder="Search categories…"
            onClear={table.clearFilters}
            isFiltered={table.isFiltered}
          />
        }
      />

      <ConfirmDialog
        open={pendingDelete != null}
        onOpenChange={(open) => !open && setPendingDelete(null)}
        title="Delete this category"
        description={
          <>
            <span className="font-medium">{pendingDelete?.name}</span> will be removed. Posts in it
            are NOT deleted — they simply lose their category.
          </>
        }
        confirmLabel="Delete category"
        tone="danger"
        onConfirm={async () => {
          if (!pendingDelete) return;
          await removeCategory.mutateAsync(pendingDelete.id);
          setPendingDelete(null);
        }}
      />
    </div>
  );
}
